/**
 * Value for the `X-GitHub-Api-Version` header on every REST call.
 *
 * Must stay within 24 months of its release date; `GET /versions` on the
 * instance lists the versions it accepts.
 */
export const GITHUB_API_VERSION = '2026-03-10';

/** Default `Accept` media type for REST calls: JSON resources. */
export const GITHUB_JSON_MEDIA_TYPE = 'application/vnd.github+json';

/**
 * `Accept` media type that makes `GET /repos/{owner}/{repo}/contents/{path}`
 * return the file rendered to HTML, so Markdown is rendered without uploading
 * its text in a request body.
 */
export const GITHUB_HTML_MEDIA_TYPE = 'application/vnd.github.html+json';

/**
 * GitHub Enterprise Cloud with data residency (`*.ghe.com`) serves its REST API
 * from `api.<host>`, while GitHub Enterprise Server serves it from `<host>/api/v3`.
 */
export type GitHubEndpoints = {
  webBaseUrl: string;
  apiBaseUrl: string;
  rawBaseUrl: string;
};

/** Endpoints for a bare hostname, with no `integrations.github` entry to consult. */
export function endpointsForHost(host: string): GitHubEndpoints {
  const webBaseUrl = `https://${host}`;

  if (host === 'github.com') {
    return {
      webBaseUrl,
      apiBaseUrl: 'https://api.github.com',
      rawBaseUrl: 'https://raw.githubusercontent.com',
    };
  }

  if (host.endsWith('.ghe.com')) {
    return {
      webBaseUrl,
      apiBaseUrl: `https://api.${host}`,
      rawBaseUrl: `https://raw.${host}`,
    };
  }

  return {
    webBaseUrl,
    apiBaseUrl: `${webBaseUrl}/api/v3`,
    rawBaseUrl: `${webBaseUrl}/raw`,
  };
}

/**
 * Endpoints for an `enterpriseInstanceUrl` as configured on the GitHub auth
 * provider. An absent value means public GitHub.
 */
export function resolveGitHubEndpoints(
  enterpriseInstanceUrl?: string,
): GitHubEndpoints {
  if (!enterpriseInstanceUrl) {
    return endpointsForHost('github.com');
  }
  return endpointsForHost(new URL(enterpriseInstanceUrl).hostname);
}
